import React, { useState, useRef, useEffect } from "react";
import { Zap, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import type { PortalShellQuickActionsProps, PortalShellQuickActionItem } from "../../contracts/portal-shell";

export function PortalShellQuickActions({
  title = "Quick Actions",
  description,
  triggerLabel,
  triggerAriaLabel = "Quick Actions",
  items,
  onAction,
  className,
}: PortalShellQuickActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderItem = (item: PortalShellQuickActionItem) => {
    return (
      <button
        key={item.id}
        onClick={() => {
          if (!item.disabled) {
            onAction(item.id);
            setIsOpen(false);
          }
        }}
        disabled={item.disabled}
        className={cn(
          "flex w-full items-start gap-3 rounded-control px-3 py-2 text-sm transition-colors text-left",
          "hover:bg-accent hover:text-accent-foreground",
          item.disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {item.icon && (
          <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground group-hover:text-foreground">
            {item.icon}
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <span className="font-medium truncate">{item.label}</span>
          {item.description && (
            <span className="truncate text-xs text-muted-foreground">
              {item.description}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative", className)}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={triggerAriaLabel}
        className={cn(
          "inline-flex items-center justify-center rounded-control text-sm font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-ring",
          triggerLabel 
            ? "border border-border bg-card px-3 py-1.5 gap-2 hover:bg-muted/50"
            : "h-9 w-9 border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
        )}
      >
        {triggerLabel ? (
          <>
            <Zap className="h-4 w-4 text-primary fill-primary/20" />
            <span>{triggerLabel}</span>
            <ChevronDown className={cn(
              "h-3 w-3 text-muted-foreground transition-transform duration-200",
              isOpen && "rotate-180"
            )} />
          </>
        ) : (
          <Zap className="h-4 w-4" />
        )}
      </button>

      {isOpen && (
        <div className={cn(
          "absolute right-0 top-full z-50 mt-1 flex w-64 flex-col overflow-hidden rounded-panel border border-border bg-card shadow-lg animate-in fade-in zoom-in-95 duration-100"
        )}>
          <div className="flex flex-col border-b border-border bg-muted/30 px-4 py-3">
            <h3 className="text-xs font-semibold text-foreground">{title}</h3>
            {description && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {description}
              </p>
            )}
          </div>
          
          <div className="p-1.5 space-y-0.5">
            {items.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground">
                No actions available.
              </div>
            ) : (
              items.map(renderItem)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
